import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Grid,
  Typography
} from '@mui/material';
import { useMutation } from 'react-query';
import { toast } from 'sonner';
import client from '../../../../../API';

const PartnershipDialog = ({ open, onClose, info }) => {
  const [formData, setFormData] = useState({
    name: '',
    creator: '',
    description: '',
    email: '',
    status: 'pending',
    workflow_stage: 'negotiation',
    po_number: '',
    drive_link: '',
    notes: '',
    interest_rate: 5,
    unique_code: ''
  });

  // Initialize form data when editing
  useEffect(() => {
    if (info) {
      setFormData({
        name: info.name || '',
        creator: info.creator || '',
        description: info.description || '',
        email: info.email || '',
        status: info.status || 'pending',
        workflow_stage: info.workflow_stage || 'negotiation',
        po_number: info.po_number || '',
        drive_link: info.drive_link || '',
        notes: info.notes || '',
        interest_rate: info.interest_rate || 5,
        unique_code: info.unique_code || ''
      });
    }
  }, [info]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const updatePartnershipMutation = useMutation(
    (data) => client.partnerships.update(info.id, data),
    {
      onSuccess: () => {
        toast.success('Partnership updated successfully');
        onClose(true); // true indicates refresh needed
      },
      onError: (error) => {
        console.error('Error updating partnership:', error);
        toast.error('Failed to update partnership', {
          description: error?.response?.data?.error || error.message
        });
      }
    }
  );

  const handleSubmit = () => {
    // Basic validation
    if (!formData.name || !formData.creator || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (info) {
      // Editing existing partnership
      updatePartnershipMutation.mutate(formData);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        Edit Partnership
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Partnership Name"
                required
                value={formData.name}
                onChange={handleChange('name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Creator ID"
                required
                value={formData.creator}
                onChange={handleChange('creator')}
                helperText="Creator unique identifier"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange('description')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                required
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="PO Number"
                value={formData.po_number}
                onChange={handleChange('po_number')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleChange('status')}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Workflow Stage</InputLabel>
                <Select
                  value={formData.workflow_stage}
                  label="Workflow Stage"
                  onChange={handleChange('workflow_stage')}
                >
                  <MenuItem value="negotiation">Negotiation</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="production">Production</MenuItem>
                  <MenuItem value="review">Review</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Interest Rate (%)"
                type="number"
                value={formData.interest_rate}
                onChange={handleChange('interest_rate')}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Unique Code"
                value={formData.unique_code}
                onChange={handleChange('unique_code')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Drive Link"
                value={formData.drive_link}
                onChange={handleChange('drive_link')}
                placeholder="https://drive.google.com/..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange('notes')}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updatePartnershipMutation.isLoading}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartnershipDialog;